import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Avatar
} from "@mui/material";
import {
  Payment,
  PaymentWithRelations,
  useGetFinanceOutstandingLeviesQuery,
  useGetPaymentsAdminQuery,
  useGetFinanceRevenuesQuery
} from "../../redux/residentPortalApi";
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import {
  AttachMoney,
  FlashOn,
  Home,
  LocalDrink,
  LocalGasStation,
  Payment as PaymentIcon,
  TrendingDown,
  TrendingUp
} from "@mui/icons-material";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/currencyUtil";
import { developmentInfo, formatPropertyNumber, formatPropertyType } from "../../utils/propertyUtils";
import { capitalise } from "../../utils/stringUtil";

type PaymentMetadata = {
  reconciliationAudit?: {
    reconciled?: boolean;
    actorType?: string;
    actorName?: string;
    reconciledAt?: string;
  };
};

const COLORS = [
  "#1976d2",
  "#ff9800",
  "#4caf50",
  "#e53935",
  "#9c27b0",
];

const timeRanges = [
  { label: "All Time", value: "all" },
  { label: "This Month", value: "this_month" },
  { label: "Last Month", value: "last_month" },
  { label: "This Quarter", value: "this_quarter" },
  { label: "Last Quarter", value: "last_quarter" },
  { label: "This Year", value: "this_year" },
  { label: "Last Year", value: "last_year" },
];

const FinanceDashboard: React.FC = () => {
  const [selectedRange, setSelectedRange] = useState("all");
  const selectedRangeLabel =timeRanges.find((range) => range.value === selectedRange)?.label || "All Time";
  const [visiblePayments, setVisiblePayments] = useState(10);

 const { data: revenues, isLoading: revenueLoading } = useGetFinanceRevenuesQuery(
  { range: selectedRange }
 );

  const { data: payments, isLoading: paymentsLoading } = useGetPaymentsAdminQuery({
    limit: -1,
    skip: 0,
  });
  const { data: outstandingLevies, isLoading: outstandingLeviesLoading } = useGetFinanceOutstandingLeviesQuery();

  if (revenueLoading || paymentsLoading || outstandingLeviesLoading) {
    return (
      <Box sx={{ 
        display: "flex", 
        flexDirection: "column",
        justifyContent: "center", 
        alignItems: "center", 
        height: "50vh",
        gap: 2
      }}>
        <CircularProgress size={60} thickness={4} sx={{ color: 'primary.main' }} />
        <Typography variant="h6" color="primary" sx={{ mt: 2, fontWeight: 'medium' }}>
          Loading Financial Data...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          We're preparing your financial overview. This will only take a moment.
        </Typography>
      </Box>
    );
  }

  if (!revenues || !payments) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        p: 4, 
        bgcolor: 'error.light', 
        borderRadius: 2,
        mt: 4,
        maxWidth: '600px',
        mx: 'auto'
      }}>
        <Typography variant="h6" color="error.dark">
          No revenue or payment data available
        </Typography>
        <Typography variant="body2" color="error.dark" sx={{ mt: 2, textAlign: 'center' }}>
          We couldn't retrieve the financial data. This could be due to a connection issue or because no data exists yet.
          Please try refreshing the page or contact support if the problem persists.
        </Typography>
      </Box>
    );
  }

  const normalizedRevenues = Object.fromEntries(
    Object.entries(revenues).map(([key, amount]) => [key, amount / 100])
  );

  const totalRevenue = Object.values(normalizedRevenues).reduce((sum, amount) => sum + amount, 0);

  const chartData = Object.entries(normalizedRevenues).map(([key, amount], index) => ({
    category: key,
    amount,
    color: COLORS[index % COLORS.length],
  }));

  const paymentMethodsData = Object.entries(
    payments.reduce((acc: Record<string, number>, p: Payment) => {
      const method = p.paymentMethod ?? "Unknown";
      acc[method] = (acc[method] || 0) + p.amount / 100;
      return acc;
    }, {})
  ).map(([method, total], index) => ({
    method,
    total,
    color: COLORS[index % COLORS.length]
  }));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", margin: 5 }}>
      <Paper sx={{ width: '100%', p: 4, mb: 4, borderRadius: 4, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 50%, #90caf9 100%)', color: 'white', boxShadow: 6 }}>
        <Stack spacing={2} direction="row" width="100%" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Box sx={{ mb: { xs: 2, md: 0 } }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              Finance Dashboard
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              A quick overview of revenues, payments and levies
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel sx={{ color: 'white' }}>Time Range</InputLabel>
              <Select
                value={selectedRange}
                onChange={(e) => setSelectedRange(e.target.value)}
                label={'Time Range'}
                sx={{
                  color: 'white',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.6)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                  '.MuiSvgIcon-root': { color: 'white' },
                }}
              >
                {timeRanges.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Paper sx={{ px: 3, py: 2, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.35)' }} elevation={0}>
              <Typography variant="overline" sx={{ opacity: 0.85 }}>
                Total Revenue
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1 }}>
                {formatCurrency(totalRevenue)}
              </Typography>
            </Paper>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2} justifyContent="center">
        {Object.entries(normalizedRevenues).map(([key, amount], index) => (
          <Grid item xs={12} sm={6} md={3} key={key}>
            <Paper sx={{
              p: 3,
              textAlign: "center",
              background: `linear-gradient(135deg, ${chartData[index].color} 0%, ${chartData[index].color}cc 100%)`,
              color: "white",
              borderRadius: 4,
              boxShadow: 4,
              transition: "transform 0.25s ease, box-shadow 0.25s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: 8,
                cursor: "pointer",
              },
            }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56, mx: 'auto', mb: 1, border: '1px solid rgba(255,255,255,0.35)' }}>
                {key === "sales" && <TrendingUp sx={{ color: "white" }} />}
                {key === "expenses" && <TrendingDown sx={{ color: "white" }} />}
                {key === "payments" && <PaymentIcon sx={{ color: "white" }} />}
                {key === "water" && <LocalDrink sx={{ color: "white" }} />}
                {key === "electricity" && <FlashOn sx={{ color: "white" }} />}
                {key === "gas" && <LocalGasStation sx={{ color: "white" }} />}
                {key === "levy" && <Home sx={{ color: "white" }} />}
                {!["sales", "expenses", "payments", "water", "electricity", "gas", "levy"].includes(key) &&
                  <AttachMoney sx={{ color: "white" }} />}
              </Avatar>
              <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>{selectedRangeLabel}</Typography>
              <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 700 }}>{key.charAt(0).toUpperCase() + key.slice(1)}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{formatCurrency(amount)}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={4} justifyContent="space-between" mt={5}>
        {/* Payments Table */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Payments</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Payment Date</strong></TableCell>
                    <TableCell><strong>Purpose</strong></TableCell>
                    <TableCell><strong>Method</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Property Ref</strong></TableCell>
                    <TableCell><strong>Reconciliation</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.slice(0, visiblePayments).map((payment: PaymentWithRelations) => {
                    const metadata = (payment.metadata ?? {}) as PaymentMetadata;
                    const reconciliationAudit = metadata.reconciliationAudit;
                    const wasAgentReconciled = reconciliationAudit?.reconciled === true;

                    return (
                      <TableRow key={payment.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' } }}>
                        <TableCell>{dayjs(payment.paymentDate).format("DD/MM/YYYY")}</TableCell>
                        <TableCell>{capitalise(payment.purpose)}</TableCell>
                        <TableCell>{capitalise(payment.paymentMethod)}</TableCell>
                        <TableCell align="right">{formatCurrency(payment.amount / 100)}</TableCell>
                        <TableCell>{payment?.property && formatPropertyNumber(payment?.property)}</TableCell>
                        <TableCell>
                          {wasAgentReconciled ? (
                            <Box>
                              <Chip
                                size="small"
                                color="warning"
                                label={capitalise(reconciliationAudit?.actorName ?? 'Inspired AI Agent')}
                              />
                              {reconciliationAudit?.reconciledAt && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {dayjs(reconciliationAudit.reconciledAt).format("DD/MM/YYYY HH:mm")}
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Standard flow
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {visiblePayments < payments.length && (
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => setVisiblePayments(prev => prev + 10)}
                >
                  Load More
                </Button>
              </Box>
            )}
          </Paper>

          {/* Outstanding Levies */}
          <Paper sx={{ mt: 5, p: 3, boxShadow: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Outstanding Levies</Typography>
            {outstandingLevies ? (
              <>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                  Total Outstanding: {formatCurrency(Math.abs(outstandingLevies.totalOutstanding ?? 0))}
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Development</strong></TableCell>
                        <TableCell><strong>Phase</strong></TableCell>
                        <TableCell><strong>Amount</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(outstandingLevies?.totalByDevelopmentAndPhase ?? {}).map(([development, phases]) => (
                        <React.Fragment key={development}>
                          <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell><strong>{developmentInfo(development).name}</strong></TableCell>
                            <TableCell><i>All Phases</i></TableCell>
                            <TableCell><strong>{formatCurrency(Math.abs(outstandingLevies?.totalByDevelopment?.[development] ?? 0))}</strong></TableCell>
                          </TableRow>
                          {Object.entries(phases ?? {}).map(([phase, amount]) => (
                            <TableRow key={`${development}-${phase}`}>
                              <TableCell></TableCell>
                              <TableCell>{formatPropertyType(phase)}</TableCell>
                              <TableCell>{formatCurrency(Math.abs(amount))}</TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Typography variant="body1">Loading outstanding levies...</Typography>
            )}
          </Paper>
        </Grid>

        {/* Donut Charts */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Service Breakdown</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="amount"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  innerRadius={70}
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>

          <Paper sx={{ mt: 5, p: 3, boxShadow: 3, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Payment Methods Breakdown</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={paymentMethodsData}
                  dataKey="total"
                  nameKey="method"
                  cx="50%"
                  cy="50%"
                  outerRadius={110}
                  innerRadius={70}
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {paymentMethodsData.map((_entry, index) => (
                    <Cell key={`cell-method-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value as number)} />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FinanceDashboard;
